import { useState, useMemo, useEffect } from "react";

const FIREBASE_URL = "https://engneeringpc-default-rtdb.asia-southeast1.firebasedatabase.app/projects.json";

const DEPARTMENTS = ["All","IT","AIML","Mechatronics","Electronics"];

const deptColor = {
  it:"#818CF8", aiml:"#34D399", mechatronics:"#F59E0B", electronics:"#F87171"
};

const deptLabel = {
  it:"IT", aiml:"AIML", mechatronics:"MECHATRONICS", electronics:"IOT"
};

const DEFAULT_TECH = {
  it: ["React.js","Node.js"],
  aiml: ["Python","TensorFlow"],
  mechatronics: ["Arduino","Sensors"],
  electronics: ["React.js","IoT Core AWS"]
};

function normalizeDept(d){ return (d||"").toLowerCase(); }

function getTechnologies(proj){
  if (Array.isArray(proj.technologies) && proj.technologies.length) return proj.technologies;
  return DEFAULT_TECH[normalizeDept(proj.department)] || ["React.js","IoT Core AWS"];
}

// ---- Professional thumbnail: overflows the card (top/bottom/left), fades into it on the right ----
function ProjectThumb({ imageUrl, title, department, rowBg }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [hover, setHover] = useState(false);
  const color = deptColor[normalizeDept(department)] || "#818CF8";
  const showFallback = !imageUrl || errored;

  const imgScale = loaded ? (hover ? 1.08 : 1) : 1.12;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 80,          // reduced from 104
        height: 110,        // reduced from 144
        borderRadius: 12,
        flexShrink: 0,
        overflow: "hidden",
        position: "relative",
        background: showFallback
          ? `linear-gradient(135deg, ${color}55, ${color}15 60%, ${color}05)`
          : rowBg,
        boxShadow: hover
          ? `0 8px 20px -6px rgba(0,0,0,0.5), 0 0 0 1px ${color}55`
          : "0 4px 12px -4px rgba(0,0,0,0.4)",
        transition: "box-shadow 0.35s ease",
      }}
    >
      {!showFallback && (
        <img
          src={imageUrl}
          alt={title}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
            filter: loaded ? "blur(0px)" : "blur(14px)",
            transform: `scale(${imgScale})`,
            opacity: loaded ? 1 : 0.7,
            transition:
              "filter 0.5s ease, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease",
            WebkitMaskImage:
              "linear-gradient(to right, #000 0%, #000 38%, rgba(0,0,0,0.9) 55%, rgba(0,0,0,0.55) 72%, rgba(0,0,0,0.15) 88%, transparent 100%)",
            maskImage:
              "linear-gradient(to right, #000 0%, #000 38%, rgba(0,0,0,0.9) 55%, rgba(0,0,0,0.55) 72%, rgba(0,0,0,0.15) 88%, transparent 100%)",
          }}
        />
      )}
      {/* overlay – same, but adjust width slightly if needed */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "60%",
          background: `linear-gradient(to right, transparent 0%, transparent 40%, ${rowBg}55 70%, ${rowBg} 100%)`,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

// ---- NEW: Flashy "View Details" button with glow, shimmer & sliding arrow ----
function ViewDetailsButton({ isAvailable, onClick }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);

  if (!isAvailable) {
    return (
      <button
        disabled
        title="This project is already booked"
        style={{
          background:"rgba(255,255,255,0.05)",
          border:"1px solid rgba(255,255,255,0.1)",
          color:"rgba(255,255,255,0.35)",
          borderRadius:10,padding:"8px 16px",fontSize:12,fontWeight:700,
          cursor:"not-allowed",flexShrink:0,whiteSpace:"nowrap"
        }}
      >
        Booked
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      onMouseEnter={()=>setHover(true)}
      onMouseLeave={()=>{setHover(false); setPressed(false);}}
      onMouseDown={()=>setPressed(true)}
      onMouseUp={()=>setPressed(false)}
      style={{
        position:"relative",
        overflow:"hidden",
        display:"flex",alignItems:"center",gap:6,
        background: hover
          ? "linear-gradient(90deg,#6366F1,#818CF8,#A78BFA)"
          : "linear-gradient(90deg,#4F46E5,#6366F1)",
        backgroundSize: hover ? "200% 100%" : "100% 100%",
        border:"none",
        color:"#fff",
        borderRadius:10,
        padding:"9px 16px",
        fontSize:12.5,fontWeight:800,letterSpacing:"0.01em",
        cursor:"pointer",flexShrink:0,whiteSpace:"nowrap",
        boxShadow: hover
          ? "0 0 0 1px rgba(129,140,248,0.6), 0 8px 22px -4px rgba(99,102,241,0.8), 0 0 20px rgba(129,140,248,0.5)"
          : "0 0 0 1px rgba(129,140,248,0.3), 0 4px 12px -2px rgba(79,70,229,0.5)",
        transform: pressed
          ? "translateY(0) scale(0.96)"
          : hover ? "translateY(-2px) scale(1.045)" : "translateY(0) scale(1)",
        transition:"transform 0.18s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s ease, background-position 0.6s ease"
      }}
    >
      {/* shimmer sweep */}
      <span style={{
        position:"absolute",top:0,left: hover ? "130%" : "-60%",
        width:"40%",height:"100%",
        background:"linear-gradient(120deg, transparent, rgba(255,255,255,0.55), transparent)",
        transform:"skewX(-20deg)",
        transition:"left 0.65s ease"
      }}/>
      <span style={{position:"relative"}}>View Details</span>
      <span style={{
        position:"relative",
        display:"inline-block",
        transform: hover ? "translateX(4px)" : "translateX(0)",
        transition:"transform 0.25s ease"
      }}>→</span>
    </button>
  );
}

// ---- Small but eye-catching ad row (inserted every 10 items) ----
function AdRow({ onEnroll }) {
  return (
    <div style={{
      background:"linear-gradient(90deg, #4F46E5 0%, #4338CA 60%, #0F172A 100%)",
      border:"1px solid #818CF8",
      borderRadius:12,
      padding:"12px 18px",
      display:"flex",alignItems:"center",justifyContent:"space-between",
      gap:16,flexWrap:"wrap",
      position:"relative",overflow:"hidden",
      boxShadow:"0 10px 30px -10px rgba(79,70,229,0.5)"
    }}>
      <div style={{position:"absolute",top:-20,right:-20,width:90,height:90,background:"radial-gradient(circle, rgba(52,211,153,0.3), transparent 70%)"}}/>
      <div style={{display:"flex",alignItems:"center",gap:12,position:"relative"}}>
        <span style={{background:"#F59E0B",color:"#1E1B0F",fontSize:10,fontWeight:800,borderRadius:99,padding:"4px 10px",whiteSpace:"nowrap"}}>🔥 NEW BATCH</span>
        <div>
          <div style={{color:"#fff",fontWeight:800,fontSize:14}}>
            Class of <span style={{color:"#FDE68A"}}>React.js</span> + IoT
          </div>
          <div style={{color:"rgba(255,255,255,0.7)",fontSize:11.5}}>
            HTML to Redux + AWS IoT · 2 Months · Offline · Only 4 seats/batch
          </div>
        </div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:12,position:"relative"}}>
        <div style={{textAlign:"right"}}>
          <span style={{fontSize:18,fontWeight:900,color:"#34D399"}}>₹18,000</span>
          <span style={{fontSize:11,color:"rgba(255,255,255,0.5)",textDecoration:"line-through",marginLeft:6}}>₹25,000</span>
        </div>
        <button onClick={onEnroll} style={{
          background:"linear-gradient(90deg,#F59E0B,#FBBF24)",
          color:"#1E1B0F",border:"none",borderRadius:8,
          padding:"8px 16px",fontWeight:800,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"
        }}>
          Enroll Now →
        </button>
      </div>
    </div>
  );
}

// ---- Professional light-theme project details modal ----
function ProjectDetailsModal({ project, onClose }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgErrored, setImgErrored] = useState(false);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const color = deptColor[normalizeDept(project.department)] || "#6366F1";
  const label = deptLabel[normalizeDept(project.department)] || project.department;
  const technologies = getTechnologies(project);
  const isAvailable = Number(project.bookingAvailable) === 1;
  const showImgFallback = !project.imageUrl || imgErrored;

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = () => {
    setClosing(true);
    setVisible(false);
    setTimeout(onClose, 260);
  };

  const shown = visible && !closing;

  return (
    <div onClick={handleClose} style={{
      position:"fixed",inset:0,
      background: shown ? "rgba(15,23,42,0.75)" : "rgba(15,23,42,0)",
      display:"flex",alignItems:"center",justifyContent:"center",
      zIndex:1000,padding:20,backdropFilter: shown ? "blur(2px)" : "blur(0px)",
      transition:"background 0.28s ease, backdrop-filter 0.28s ease"
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"#F8FAFC",borderRadius:24,maxWidth:500,width:"100%",
        maxHeight:"90vh",overflowY:"auto",position:"relative",
        boxShadow:"0 30px 80px -20px rgba(0,0,0,0.5)",
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0) scale(1)" : "translateY(18px) scale(0.96)",
        transition:"opacity 0.32s cubic-bezier(0.16,1,0.3,1), transform 0.32s cubic-bezier(0.16,1,0.3,1)"
      }}>

        {/* ── Banner image (half the card, flush to every edge) ── */}
        <div style={{position:"relative",width:"100%",height:"42vh",minHeight:220,maxHeight:320,overflow:"hidden",borderRadius:"24px 24px 0 0"}}>
          {showImgFallback ? (
            <div style={{
              width:"100%",height:"100%",
              background:`linear-gradient(135deg, ${color}66, ${color}20 55%, ${color}0A)`
            }}/>
          ) : (
            <img
              src={project.imageUrl}
              alt={project.title}
              onLoad={()=>setImgLoaded(true)}
              onError={()=>setImgErrored(true)}
              style={{
                width:"100%",height:"100%",objectFit:"cover",display:"block",
                filter: imgLoaded ? "blur(0px)" : "blur(18px)",
                transform: imgLoaded ? "scale(1)" : "scale(1.1)",
                transition:"filter 0.6s ease, transform 0.6s ease"
              }}
            />
          )}
          {/* fade into card background at the bottom of the image */}
          <div style={{
            position:"absolute",left:0,right:0,bottom:0,height:90,
            background:"linear-gradient(to bottom, rgba(248,250,252,0), #F8FAFC)"
          }}/>
          {/* close button */}
          <button onClick={handleClose} style={{
            position:"absolute",top:16,right:16,width:40,height:40,borderRadius:"50%",
            background:"rgba(255,255,255,0.92)",border:"none",cursor:"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:16,fontWeight:700,color:"#0F172A",
            boxShadow:"0 4px 14px rgba(0,0,0,0.15)"
          }}>
            ✕
          </button>
        </div>

        {/* ── Content ── */}
        <div style={{padding:"0 28px 28px"}}>

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:10}}>
            <h2 style={{margin:0,fontSize:28,fontWeight:800,color:"#0F172A",lineHeight:1.2,letterSpacing:"-0.01em"}}>
              {project.title}
            </h2>
            <span style={{
              flexShrink:0,fontSize:11,fontWeight:800,letterSpacing:"0.03em",
              color:color,background:"#fff",border:`1.5px solid ${color}55`,
              borderRadius:10,padding:"6px 12px",whiteSpace:"nowrap",
              boxShadow:"0 2px 6px rgba(0,0,0,0.06)"
            }}>
              {label}
            </span>
          </div>

          <p style={{color:"#475569",fontSize:15,lineHeight:1.6,margin:"0 0 22px"}}>
            {project.description}
          </p>

          {/* Price / Status strip */}
          <div style={{display:"flex",gap:10,marginBottom:22}}>
            <div style={{flex:1,background:"#fff",border:"1px solid #E2E8F0",borderRadius:12,padding:"10px 14px"}}>
              <div style={{fontSize:10.5,color:"#94A3B8",fontWeight:700,letterSpacing:"0.03em",marginBottom:2}}>PRICE</div>
              <div style={{fontSize:17,fontWeight:800,color:"#0F172A"}}>₹{(project.price||0).toLocaleString()}</div>
            </div>
            <div style={{flex:1,background:"#fff",border:"1px solid #E2E8F0",borderRadius:12,padding:"10px 14px"}}>
              <div style={{fontSize:10.5,color:"#94A3B8",fontWeight:700,letterSpacing:"0.03em",marginBottom:2}}>STATUS</div>
              <div style={{fontSize:14,fontWeight:800,color: isAvailable ? "#10B981" : "#EF4444"}}>
                {isAvailable ? "● Available" : "● Booked"}
              </div>
            </div>
            {project.serialNo != null && (
              <div style={{flex:1,background:"#fff",border:"1px solid #E2E8F0",borderRadius:12,padding:"10px 14px"}}>
                <div style={{fontSize:10.5,color:"#94A3B8",fontWeight:700,letterSpacing:"0.03em",marginBottom:2}}>SERIAL</div>
                <div style={{fontSize:14,fontWeight:800,color:"#0F172A"}}>#{project.serialNo}</div>
              </div>
            )}
          </div>

          {/* Technologies used */}
          <div style={{marginBottom:26}}>
            <div style={{fontSize:11,color:"#94A3B8",fontWeight:800,letterSpacing:"0.06em",marginBottom:10}}>
              TECHNOLOGIES USED
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {technologies.map(t => (
                <span key={t} style={{
                  fontSize:13,fontWeight:600,color:"#4338CA",
                  background:"#EEF2FF",border:"1px solid #C7D2FE",
                  borderRadius:10,padding:"7px 14px",fontFamily:"monospace"
                }}>
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <a href="tel:9834455108" style={{
              flex:"1 1 200px",display:"flex",alignItems:"center",justifyContent:"center",gap:8,
              background:"linear-gradient(90deg,#4F46E5,#6366F1)",color:"#fff",
              textDecoration:"none",borderRadius:14,padding:"15px 18px",
              fontWeight:700,fontSize:15,boxShadow:"0 8px 20px -6px rgba(79,70,229,0.5)"
            }}>
              Book This Project ↗
            </a>
            <a href="tel:9834455108" style={{
              flex:"1 1 160px",display:"flex",alignItems:"center",justifyContent:"center",gap:8,
              background:"#fff",color:"#4338CA",textDecoration:"none",
              border:"1.5px solid #E2E8F0",borderRadius:14,padding:"15px 18px",
              fontWeight:700,fontSize:15
            }}>
              Get a Quote →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProjectList() {
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [department, setDepartment] = useState("All");
  const [sortBy, setSortBy] = useState("dept_asc");
  const [showAd, setShowAd] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetch(FIREBASE_URL)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch projects");
        return res.json();
      })
      .then(data => {
        if (!data) { setAllProjects([]); return; }
        const list = Object.entries(data).map(([id, val]) => ({ id, ...val }));
        setAllProjects(list);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = department === "All"
      ? [...allProjects]
      : allProjects.filter(p => normalizeDept(p.department) === department.toLowerCase());

    if (sortBy === "price_low") list.sort((a,b)=>(a.price||0)-(b.price||0));
    else if (sortBy === "price_high") list.sort((a,b)=>(b.price||0)-(a.price||0));
    else list.sort((a,b)=> normalizeDept(a.department).localeCompare(normalizeDept(b.department)));

    return list;
  }, [allProjects, department, sortBy]);

  // Inject ad row after every 10 project rows
  const rows = [];
  filtered.forEach((proj, i) => {
    rows.push({ type:"project", data: proj, key:`p-${proj.id}` });
    if ((i + 1) % 10 === 0) rows.push({ type:"ad", key:`ad-${i}` });
  });

  return (
    <div style={{background:"#0B1120",minHeight:"100vh",padding:"40px 24px"}}>
      <div style={{maxWidth:1000,margin:"0 auto"}}>

        {/* Page Title */}
        <div style={{textAlign:"center",marginBottom:36}}>
          <div style={{
            display:"inline-flex",alignItems:"center",gap:8,
            background:"rgba(79,70,229,0.15)",border:"1px solid rgba(79,70,229,0.3)",
            borderRadius:99,padding:"5px 14px",fontSize:12,color:"#818CF8",
            fontWeight:700,marginBottom:14
          }}>
            🎓 ACADEMIC YEAR 2026-27
          </div>
          <h1 style={{margin:0,fontSize:34,fontWeight:900,color:"#fff",letterSpacing:"-0.02em"}}>
            Engineering & M.Tech Projects
          </h1>
          <p style={{color:"rgba(255,255,255,0.5)",fontSize:14,marginTop:8}}>
            Browse ready-to-build project ideas across departments
          </p>
        </div>

        {/* Filters */}
        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:20,justifyContent:"center"}}>
          <select value={department} onChange={e=>setDepartment(e.target.value)} style={{
            background:"#1E293B",color:"#fff",border:"1px solid rgba(255,255,255,0.15)",
            borderRadius:10,padding:"9px 14px",fontSize:13,fontWeight:600,cursor:"pointer"
          }}>
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>

          <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{
            background:"#1E293B",color:"#fff",border:"1px solid rgba(255,255,255,0.15)",
            borderRadius:10,padding:"9px 14px",fontSize:13,fontWeight:600,cursor:"pointer"
          }}>
            <option value="dept_asc">Sort: Department</option>
            <option value="price_low">Sort: Price (Low → High)</option>
            <option value="price_high">Sort: Price (High → Low)</option>
          </select>
        </div>

        {/* Loading / Error states */}
        {loading && (
          <div style={{color:"rgba(255,255,255,0.5)",textAlign:"center",padding:"40px 0"}}>Loading projects...</div>
        )}
        {error && (
          <div style={{color:"#F87171",textAlign:"center",padding:"20px 0"}}>Error: {error}</div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div style={{color:"rgba(255,255,255,0.5)",textAlign:"center",padding:"40px 0"}}>No projects found.</div>
        )}

        {/* Single-line rows — extra gap so the overflowing thumbnails have room to breathe */}
        <div style={{display:"flex",flexDirection:"column",gap:32}}>
          {rows.map(row => {
            if (row.type === "ad") return <AdRow key={row.key} onEnroll={()=>setShowAd(true)} />;

            const proj = row.data;
            const isAvailable = Number(proj.bookingAvailable) === 1;
            const rowBg = isAvailable ? "#111827" : "#0D1420";

            return (
             <div key={row.key} style={{
  background: rowBg,
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 14,
  
  display: "flex",
  alignItems: "center",
  gap: 14,
  flexWrap: "wrap",
  opacity: isAvailable ? 1 : 0.55,
}}>
  <ProjectThumb
    imageUrl={proj.imageUrl}
    title={proj.title}
    department={proj.department}
    rowBg={rowBg}
  />

  {/* Department badge */}
  <span style={{
    fontSize: 10.5,
    fontWeight: 800,
    minWidth: 60,
    textAlign: "center",
    color: deptColor[normalizeDept(proj.department)] || "#9CA3AF",
    background: `${deptColor[normalizeDept(proj.department)] || "#9CA3AF"}22`,
    border: `1px solid ${deptColor[normalizeDept(proj.department)] || "#9CA3AF"}55`,
    borderRadius: 99,
    padding: "3px 10px",
    textTransform: "capitalize",
    flexShrink: 0,
  }}>
    {proj.department}
  </span>

  {/* Title & description */}
  <div style={{ flex: "1 1 220px", minWidth: 160 }}>
    <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
      {proj.title}
    </div>
    <div style={{
      color: "rgba(255,255,255,0.5)",
      fontSize: 12.5,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    }}>
      {proj.description}
    </div>
  </div>

  {/* Action button */}
  <ViewDetailsButton
    isAvailable={isAvailable}
    onClick={() => setSelectedProject(proj)}
  />

  {/* Status and price – unchanged */}
  <span style={{
    color: isAvailable ? "#34D399" : "#F87171",
    fontSize: 11.5,
    fontWeight: 700,
    
    flexShrink: 0,
    whiteSpace: "nowrap"
  }}>
    {isAvailable ? "● Available" : "● Booked"}
  </span>

  <span style={{
    color: "#34D399",
    fontWeight: 800,
    fontSize: 15,
    padding: "2px 16px",
    flexShrink: 0,
    minWidth: 70,
    textAlign: "right",
    marginLeft: "auto"
  }}>
    ₹{(proj.price || 0).toLocaleString()}
  </span>
</div>
            );
          })}
        </div>
      </div>

      {/* Project Details Popup — professional design */}
      {selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          onClose={()=>setSelectedProject(null)}
        />
      )}

      {/* Enroll modal (React course ad) */}
      {showAd && (
        <div onClick={()=>setShowAd(false)} style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20
        }}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:"#0F172A",border:"1px solid rgba(129,140,248,0.3)",
            borderRadius:20,maxWidth:420,width:"100%",padding:28,textAlign:"center"
          }}>
            <h3 style={{color:"#fff",margin:"0 0 8px"}}>React.js + IoT Batch</h3>
            <p style={{color:"rgba(255,255,255,0.6)",fontSize:13,margin:"0 0 18px"}}>
              ₹18,000 · 2 Months · Offline · Only 4 seats/batch
            </p>
            <a href="tel:9834455108" style={{
              display:"block",background:"linear-gradient(90deg,#34D399,#10B981)",
              color:"#052E22",textDecoration:"none",borderRadius:12,
              padding:"14px",fontWeight:900,fontSize:15
            }}>
              📞 Call Now: 9834455108
            </a>
          </div>
        </div>
      )}
    </div>
  );
}